import { useResidentDashboardOverview } from '@/features/residents/hooks/useDashboardQuery';
import { useCreateAnnouncement } from '@/features/residents/hooks/useDashboardMutations';
import type { CreateResidentAnnouncementPayload } from '@/features/residents/types/dashboard';
import { Button, Dropdown, Input, ModalWrapper } from '@/shared';
import type { DropdownOption } from '@/shared/components/ui/Dropdown';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useState } from 'react';
import { Controller, useForm, type Resolver } from 'react-hook-form';
import * as yup from 'yup';
import { residentApi } from '@/features/residents/api/residentApi';
import toast from 'react-hot-toast';

// -----------------------------
// Dropdown options (example values)
// -----------------------------
const categoryOptions: DropdownOption<string>[] = [
  { label: 'Maintenance', value: 'maintenance' },
  { label: 'Community', value: 'community' },
  { label: 'Billing', value: 'billing' },
];

const priorityOptions: DropdownOption<string>[] = [
  { label: 'High', value: 'HIGH' },
  { label: 'Medium', value: 'MEDIUM' },
  { label: 'Low', value: 'LOW' },
];

// -----------------------------
// Types & Schema
// -----------------------------
interface AddResidentAnnouncementModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ResidentAnnouncementFormData {
  title: string;
  description: string;
  category: string;
  priority: string;
  fileUrl?: FileList | null;
}

const residentAnnouncementSchema = yup.object({
  title: yup.string().required('Title is required'),
  description: yup.string().required('Details are required'),
  category: yup.string().required('Category is required'),
  priority: yup
    .string()
    .oneOf(['HIGH', 'MEDIUM', 'LOW'], 'Priority must be HIGH, MEDIUM, or LOW')
    .required('Priority is required'),
  fileUrl: yup.mixed<FileList>().nullable().optional(),
});

// -----------------------------
// Component
// -----------------------------
export const AddResidentAnnouncementModal: React.FC<
  AddResidentAnnouncementModalProps
> = ({ isOpen, onClose }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFileUrl, setUploadedFileUrl] = useState<string | undefined>(
    undefined,
  );

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ResidentAnnouncementFormData>({
    resolver: yupResolver(
      residentAnnouncementSchema,
    ) as Resolver<ResidentAnnouncementFormData>,
    defaultValues: { fileUrl: null },
  });

  const mutation = useCreateAnnouncement();
  const { data: overview, isLoading } = useResidentDashboardOverview();

  const handleFileUpload = async () => {
    const fileList = watch('fileUrl');
    if (!fileList || fileList.length === 0) return;

    const file = fileList[0];
    setIsUploading(true);

    try {
      // 1. Get Signed URL using resident-specific API
      const { signedUrl, path } = await residentApi.getResidentUploadUrl(
        file.name,
        'announcements',
        file.type,
      );

      // 2. Upload to Storage
      const uploadResponse = await fetch(signedUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed with status: ${uploadResponse.status}`);
      }

      setUploadedFileUrl(path);
      toast.success('File uploaded successfully');
    } catch (error) {
      console.error('File upload failed:', error);
      toast.error('Failed to upload file');
    } finally {
      setIsUploading(false);
    }
  };

  const onSubmit = (data: ResidentAnnouncementFormData) => {
    const payload: CreateResidentAnnouncementPayload = {
      ...data,
      fileUrl: uploadedFileUrl ? [uploadedFileUrl] : null,
      priority: data.priority as 'HIGH' | 'MEDIUM' | 'LOW',
    };
    console.log('Resident Announcement Payload:', payload);
    mutation.mutate(payload, {
      onSuccess: () => {
        onClose();
        reset();
        setUploadedFileUrl(undefined);
        toast.success('Announcement created successfully');
      },
      onError: () => {
        toast.error('Failed to create announcement');
      },
    });
  };

  return (
    <ModalWrapper isOpen={isOpen} onClose={onClose}>
      <div className="flex items-center justify-center min-h-screen md:p-5">
        <div className="bg-white rounded-4xl p-4 md:p-8 max-w-2xl w-full">
          <h3 className="text-xl md:text-3xl font-bold mb-2 text-text-primary">
            Add Resident Announcement
          </h3>
          <p className="mb-4 md:mb-8 text-base md:text-xl text-text-secondary">
            Notify residents efficiently and easily
          </p>

          {/* Resident Details Header */}
          <div className="mb-4">
            {isLoading ? (
              <div className="animate-pulse">
                <div className="h-8 bg-gray-200 rounded w-1/2 mb-2"></div>
                <div className="h-6 bg-gray-200 rounded w-1/3"></div>
              </div>
            ) : (
              <>
                <h3 className="text-xl md:text-2xl font-bold text-text-primary">
                  {overview?.house?.name || 'No House Assigned'}
                </h3>
                <span className="text-base md:text-xl text-text-secondary">
                  {overview?.house?.address || 'N/A'}
                </span>
              </>
            )}
          </div>

          {/* Form */}
          <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
            {/* Title */}
            <Input
              label="Title"
              placeholder="Enter title"
              {...register('title')}
              error={errors.title?.message}
              className="!text-base md:!text-xl"
            />

            {/* Details */}
            <div className="flex flex-col gap-1">
              <label className="font-medium text-text-secondary">Details</label>
              <textarea
                {...register('description')}
                placeholder="Enter announcement details"
                className="w-full rounded-md border border-gray-300 p-3 text-base md:text-xl h-[160px] focus:outline-none"
              />
              {errors.description?.message && (
                <p className="text-red-500 text-sm">
                  {errors.description.message}
                </p>
              )}
            </div>

            {/* Category */}
            <Controller
              name="category"
              control={control}
              render={({ field }) => (
                <Dropdown
                  label="Category"
                  buttonClassName="!w-full"
                  options={categoryOptions}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            {errors.category && (
              <p className="text-red-500 text-sm">{errors.category.message}</p>
            )}

            {/* Priority */}
            <Controller
              name="priority"
              control={control}
              render={({ field }) => (
                <Dropdown
                  label="Priority"
                  buttonClassName="!w-full"
                  options={priorityOptions}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            {errors.priority && (
              <p className="text-red-500 text-sm">{errors.priority.message}</p>
            )}

            {/* File Upload */}
            <div>
              <input
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                {...register('fileUrl')}
                className="hidden"
                id="file-upload"
                onChange={(e) => {
                  register('fileUrl').onChange(e);
                  setUploadedFileUrl(undefined); // Reset upload status on new file selection
                }}
              />
              {!watch('fileUrl') || watch('fileUrl')?.length === 0 ? (
                <label htmlFor="file-upload">
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:bg-gray-50 transition-colors">
                    <div className="flex flex-col gap-4 items-center justify-center">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M12 13.5V3"
                          stroke="#141414"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M20.25 13.5V19.5H3.75V13.5"
                          stroke="#141414"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M8.25 6.75L12 3L15.75 6.75"
                          stroke="#141414"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <p className="text-sm">
                        Click to choose a file or drag here
                      </p>
                      <p className="text-xs text-gray-500 mt-2">
                        Size limit: 10MB
                      </p>
                    </div>
                  </div>
                </label>
              ) : (
                <div className="mt-4 p-4 border rounded-lg bg-gray-50">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-white rounded-md border">
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M13 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V9L13 2Z"
                            stroke="#4B5563"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {watch('fileUrl')?.[0]?.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {(
                            (watch('fileUrl')?.[0]?.size || 0) /
                            (1024 * 1024)
                          ).toFixed(2)}{' '}
                          MB
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={async () => {
                        if (uploadedFileUrl) {
                          try {
                            // Note: deleteTempFile is still in resourceApi as it's a generic operation
                            const { resourceApi } = await import('@/features/resources/api/resourceApi');
                            await resourceApi.deleteTempFile(
                              uploadedFileUrl,
                            );
                            toast.success('File removed successfully');
                          } catch (error) {
                            console.error('Failed to delete file:', error);
                            toast.error('Failed to remove file');
                          }
                        }
                        setValue('fileUrl', null);
                        setUploadedFileUrl(undefined);
                        // Also clear the input value to allow selecting the same file again
                        const input = document.getElementById(
                          'file-upload',
                        ) as HTMLInputElement;
                        if (input) input.value = '';
                      }}
                      className="text-gray-400 hover:text-red-500"
                    >
                      <span className="sr-only">Remove</span>
                      <svg
                        className="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  </div>

                  {!uploadedFileUrl ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={handleFileUpload}
                      disabled={isUploading}
                    >
                      {isUploading ? 'Uploading...' : 'Upload File'}
                    </Button>
                  ) : (
                    <div className="flex items-center justify-center p-2 bg-green-50 text-green-700 rounded text-sm font-medium">
                      <svg
                        className="w-4 h-4 mr-2"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      File Verified & Uploaded
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Submit */}
            <Button
              variant="primary"
              size="lg"
              type="submit"
              className="w-full !font-bold !text-base md:!text-xl"
            >
              Publish Now
            </Button>
          </form>
        </div>
      </div>
    </ModalWrapper>
  );
};
